import { useState } from 'react'
import { layoutCheckpointGraph, shortId, colorForUser, initials } from '../../lib/versionHistory.js'
import { isCreationNode, serializeNodeData } from '../../lib/vcMenuHelpers.js'
import { C, fmtDate, fmtTime } from './vcStyles.js'
import GraphNodeMenu from './GraphNodeMenu.jsx'

// GraphView — the version-control commit graph (PlayCanvas-style). Renders the
// checkpoint DAG as an SVG: one lane (column) per branch, newest at top, edges
// to parents. Box nodes show description / "date · shortid · author" / branch
// label, matching PlayCanvas's node content. In item-history mode each node
// carries a vcAction and gets a prefix marker ([+] added, [x] removed, [=]
// modified, created for the baseline creation entry). Layout comes from the pure,
// unit-tested layoutCheckpointGraph.
//
// Right-click on a node to open a context menu with: Set as Compare Base,
// New Branch, Restore, Hard Reset, Copy Data. Selected node gets an orange border.
const NODE_W = 210
const NODE_H = 60
const LANE_W = NODE_W + 40
const ROW_H = NODE_H + 26
const PAD = 28

const PREFIX = { added: '[+]', removed: '[x]', modified: '[=]', created: '[+]' }

export default function GraphView({
  checkpoints, branches, selectedId: externalSelectedId, onSelect, itemHistory,
  onRestore, onHardReset, onNewBranch, onViewChanges, onCompare,
}) {
  const [internalSelectedId, setInternalSelectedId] = useState(null)
  const [compareBaseId, setCompareBaseId] = useState(null)
  const [menuNode, setMenuNode] = useState(null)
  const [menuPos, setMenuPos] = useState(null)

  // Use external selection only when a real id is supplied; null/undefined
  // (the mount points pass selectedId={null}) falls back to internal click
  // selection so nodes highlight on click.
  const selectedId = externalSelectedId != null ? externalSelectedId : internalSelectedId

  const { nodes, edges, laneCount } = layoutCheckpointGraph(checkpoints, branches)
  if (!nodes.length) {
    return <div style={{ color: C.textDim, padding: 24 }}>No checkpoints to graph.</div>
  }

  const branchName = new Map((branches || []).map((b) => [b.id, b.name]))
  const cx = (lane) => PAD + lane * LANE_W + NODE_W / 2
  const top = (row) => PAD + row * ROW_H
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const width = PAD * 2 + (laneCount - 1) * LANE_W + NODE_W
  const height = PAD * 2 + (nodes.length - 1) * ROW_H + NODE_H

  // In item-history mode the parents of changed checkpoints are usually
  // filtered out, so the parent edges don't connect. Thread consecutive
  // versions (row r → r+1) into a timeline instead, like PlayCanvas does.
  const threadEdges = itemHistory
    ? nodes.slice(0, -1).map((n, i) => ({ from: nodes[i + 1].id, to: n.id }))
    : edges

  const handleNodeClick = (node) => {
    setInternalSelectedId(node.id)
    onSelect?.(node)
  }

  const handleNodeContextMenu = (e, node) => {
    e.preventDefault()
    setInternalSelectedId(node.id)
    setMenuNode(node)
    setMenuPos({ x: e.clientX, y: e.clientY })
  }

  const handleSetCompareBase = () => {
    setCompareBaseId(compareBaseId === menuNode.id ? null : menuNode.id)
  }

  const handleCompareWithBase = () => {
    const baseNode = nodes.find((n) => n.id === compareBaseId)
    if (baseNode) {
      onCompare?.(baseNode, menuNode)
      setCompareBaseId(null)
    }
  }

  const handleCopyData = () => {
    const data = serializeNodeData(menuNode, branchName)
    // Guard: clipboard API is absent in non-secure contexts / some iframes, and
    // writeText rejects on permission denial — never let it throw uncaught.
    try {
      navigator.clipboard?.writeText(JSON.stringify(data, null, 2))?.catch(() => {})
    } catch { /* clipboard unavailable — no-op */ }
  }

  return (
    <>
      <svg width={width} height={height} style={{ display: 'block' }}>
        {threadEdges.map((e, i) => {
          const a = byId.get(e.from) // parent (older, lower)
          const b = byId.get(e.to)   // child (newer, higher)
          if (!a || !b) return null
          return (
            <line
              key={i}
              x1={cx(b.lane)} y1={top(b.row) + NODE_H}
              x2={cx(a.lane)} y2={top(a.row)}
              stroke={C.borderInput} strokeWidth={1.5}
              strokeDasharray={itemHistory ? '4 3' : undefined}
            />
          )
        })}
        {nodes.map((n) => {
          const x = PAD + n.lane * LANE_W
          const y = top(n.row)
          const sel = n.id === selectedId
          const isBase = n.id === compareBaseId
          const accent = colorForUser(n.created_by || n.id)
          // `uncheckpointed` is the semantic flag for the synthetic creation-origin
          // entry (server sets it; its `action` is "added" — the item's first
          // appearance — so key creation styling on the flag, not the action string).
          const isCreation = itemHistory && n.uncheckpointed === true
          const prefix = !isCreation && itemHistory && n.vcAction ? PREFIX[n.vcAction] || '' : ''
          const displayLabel = isCreation ? 'Created' : (truncate(n.label, 26) || shortId(n.id))
          const metaLine = isCreation
            ? `${fmtDate(n.created_at)} · ${n.created_by || 'unknown'} · not yet checkpointed`
            : `${fmtDate(n.created_at)} · ${shortId(n.id)} · ${fmtTime(n.created_at)}`
          return (
            <g
              key={n.id}
              style={{ cursor: 'pointer' }}
              onClick={() => handleNodeClick(n)}
              onContextMenu={(e) => handleNodeContextMenu(e, n)}
            >
              <rect
                x={x} y={y} width={NODE_W} height={NODE_H} rx={6}
                fill={C.panel}
                stroke={sel ? C.accent : isBase ? '#ffb700' : C.borderInput}
                strokeWidth={sel || isBase ? 2 : 1}
              />
              <rect x={x} y={y} width={4} height={NODE_H} rx={2} fill={accent} />
              <text x={x + 12} y={y + 18} fill={C.text} fontSize={11.5} fontWeight={600}>
                {prefix ? prefix + ' ' : ''}{displayLabel}
              </text>
              <text x={x + 12} y={y + 35} fill={C.textDim} fontSize={9.5}>
                {metaLine}
              </text>
              <text x={x + 12} y={y + 50} fill={C.textMid} fontSize={9.5}>
                {branchName.has(n.branch_id) ? branchName.get(n.branch_id) : ''}
              </text>
              {isBase && (
                <text
                  x={x + NODE_W - 32} y={y + 10}
                  fill="#ffb700" fontSize={9} fontWeight={600}
                  textAnchor="end"
                >
                  BASE
                </text>
              )}
            </g>
          )
        })}
      </svg>

      {menuNode && menuPos && (
        <GraphNodeMenu
          node={menuNode}
          x={menuPos.x}
          y={menuPos.y}
          isCompareBase={compareBaseId === menuNode.id}
          onSetCompareBase={handleSetCompareBase}
          onNewBranch={() => { onNewBranch?.(menuNode); setMenuNode(null) }}
          onRestore={() => { onRestore?.(menuNode); setMenuNode(null) }}
          onHardReset={() => { onHardReset?.(menuNode); setMenuNode(null) }}
          onCompareWithBase={compareBaseId ? handleCompareWithBase : null}
          onCopyData={handleCopyData}
          onClose={() => setMenuNode(null)}
        />
      )}
    </>
  )
}

function truncate(s, n) {
  const str = String(s || '')
  return str.length > n ? str.slice(0, n - 1) + '…' : str
}
